import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  ActivatedRoute
} from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FormArray, FormGroup } from '@angular/forms';
import { interval, Subject, Subscription } from 'rxjs';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { environment } from '@environment/environment';
import { QuestionnaireService } from '@shared/services/questionnaire.service';
import { GridRefreshService } from '@shared/services/grid-refresh.service';
import { ConfirmDialogService } from '@shared/components/dialogs/confirm-dialog/confirm-dialog.service';
import { NotificationService } from '@shared/components/notification/notification.service';
import { DataGridConfig } from '@shared/components/data-grid/data-grid-config';
import { Notification } from '@shared/components/notification/models/notification.model';
import { AnswerSetAddQuestionDialogComponent } from './dialogs/answer-set-add-question-dialog/answer-set-add-question-dialog.component';
import { LinkRendererComponent } from '@shared/components/cell-renderers/link-renderer/link-renderer.component';
import { GenerateDocumentsDialogComponent } from './dialogs/generate-documents-dialog/generate-documents-dialog.component';
import { GeneratePDFDialogComponent } from './dialogs/generate-pdf-dialog/generate-pdf-dialog.component';
import { AnswerSetHeaderDetailsComponent } from './components/answer-set-header-details/answer-set-header-details.component';
import { HeaderService } from '@app/shared/components/header/header.service';
import { HeaderItem } from '@app/shared/components/header/header';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GuidanceNotesService } from '@shared/services/guidanceNotes.service';
import { DateFormatPipe } from '@shared/pipes/dateFormat.pipe';
import { BroadcastService, AppEventType } from '@app/shared/services/broadcast.service';
import { LocalStorageService } from '@app/shared/services/local-storage-service';
import { UserSubscriber } from '@app/shared/subscribers/user.subscriber';
import { User, IUser } from '@app/shared/models/user';
import { GenerateDocumentsService } from '@app/shared/services/generate-documents.service';

// How long to wait after the user stops typing before autosaving (ms)
const AUTOSAVE_IDLE_DELAY = 5000;

export interface TabType {
  label: string;
  fields: FormlyFieldConfig[];
}
@Component({
  selector: 'db-answer-set',
  templateUrl: './answer-set.component.html',
  styleUrls: ['./answer-set.component.scss'],
  providers: [ DateFormatPipe ]
})
export class AnswerSetComponent implements OnInit, OnDestroy {
  private id: string;
  private questionnaireId: string;
  private onDestroy$ = new Subject<void>();
  public deleteDocumentsResult: any;
  public tmpClientSiteUrl: string;
  selectedTabIndex: number = 0;
  sessionID: any = 0;
  clientViewUrl: string;
  client: any;
  clientId: string;
  answerSetName: string;
  questionnaireName: string;
  headerItems: HeaderItem;
  clientSiteId: string;
  subscription: Subscription;
  subscriptionManager = new Subscription();
  user: IUser;
  model: any = {};
  tabs: TabType[] = [];
  hasLoaded: any;
  form = new FormArray([]);
  options: FormlyFormOptions[] = [];
  pristine: boolean = true;
  CurrentSectors: string;
  addQuestionDialogRef: MatDialogRef<AnswerSetAddQuestionDialogComponent>;
  generateDocumentsDialogRef: MatDialogRef<GenerateDocumentsDialogComponent>;
  generatePDFDialogRef: MatDialogRef<GeneratePDFDialogComponent>;
  dataGridConfig: DataGridConfig;
  documentsRowData: any[];
  selectedRowData: any[] = [];
  dataLoading: boolean;
  generateDocumentStatus: string = '';
  generateDocumentInProgress: boolean = false;
  saving = false;
  autosaveStatus: 'saving' | 'saved' | null = null;
  private autosaveStatusTimer: any;
  private saveQueued = false;
  private queuedSaveSilent = true;
  private lastSavedSnapshot: string;
  private modelChanged$ = new Subject<void>();


  constructor(
    private questionnaireService: QuestionnaireService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private confirmDialogService: ConfirmDialogService,
    private notificationService: NotificationService,
    private gridRefreshService: GridRefreshService,
    private headerService: HeaderService,
    private guidanceNotesService: GuidanceNotesService,
    private broadcastService: BroadcastService,
    private _localStorageService: LocalStorageService,
    private _userSubscriber: UserSubscriber,
    private generateDocumentsService: GenerateDocumentsService
  ) {
    this.id = this.route.snapshot.paramMap.get('id');
    this.sessionID = 123;
    this.hasLoaded = false;
  }

  ngOnInit() {
    this.configureHeaderItems();
    this.getAnswerSetData();
    this.configureGrid();

    this.broadcastService.on(AppEventType.DataFetchingStarted).subscribe(() => {
      this.dataLoading = true;
    });

    this.broadcastService.on(AppEventType.DataFetchingCompleted).subscribe(() => {
      this.dataLoading = false;
    });

    // Save shortly after the user pauses typing
    this.modelChanged$.pipe(
      debounceTime(AUTOSAVE_IDLE_DELAY),
      takeUntil(this.onDestroy$)
    ).subscribe(() => {
      if (this.form.dirty || !this.pristine) {
        console.log('auto saving answer-set after pause @ ' + new Date());
        this.save(true);
      }
    });

    // Fallback so changes are still saved regularly while the user types without pausing
    interval(environment.autoSaveTimout).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(() => {
      if (this.form.dirty || !this.pristine) {
        console.log('auto saving answer-set silently @ ' + new Date());
        this.save(true);
      }
    });

    const gridRefreshSubscription = this.gridRefreshService.gridRefresh$
      .subscribe(() => {
        this.initializeRowData();
    });

    this.subscriptionManager
      .add(gridRefreshSubscription)
    this.subscriptionManager
      .add(this.subscribeToUser())
    this.subscriptionManager
      .add(this.subscribeToDocumentStatus());
  }

  private subscribeToDocumentStatus(): Subscription {
    return this.generateDocumentsService.status$.subscribe((status: string) => {
      const computedStatus = status === 'Pending' || status === 'Running' ? 'InProgress' : status;
      this.generateDocumentStatus = computedStatus;
      this.generateDocumentInProgress = this.generateDocumentStatus === 'InProgress' ? true : false;
    });
  }

  private subscribeToUser(): Subscription {
    return this._userSubscriber.user$.subscribe((user: User) => {
      this.user = new User(user);
    });
  }

  selectedTabChange($event: MatTabChangeEvent) {
    this.selectedTabIndex = $event.index;
  }

  saveAndContinue() {
    // save in the background (same as autosave) so the user can carry on with the next section
    if (!this.pristine || this.form.dirty) {
      this.save(true);
    }

    if (this.selectedTabIndex !== (this.tabs.length - 1)) {
      this.selectedTabIndex += 1;
    } else {
      window.scrollTo(0, 0);
    }
  }

  configureHeaderItems() {
    this.headerItems = new HeaderItem(AnswerSetHeaderDetailsComponent, {});
  }

  getAnswerSetData() {
    this.questionnaireService.getAnswerSetData(this.id)
      .subscribe(
        ([answerset, fields]) => {
          this.model = answerset.body;
          this.tabs = fields.body;
          this.syncFormsWithTabs();
          this.questionnaireId = answerset.questionnaireId;
          this.clientSiteId = answerset.clientSiteId;

          this.CurrentSectors = this.questionnaireService.getSector(this.model.hideExpressionValue);

          this.clientId = answerset.clientId;
          this.answerSetName = answerset.answerSetName;
          this.questionnaireName = fields.questionnaireName;

          this.updateSessionID();

          this.questionnaireService.getClient(this.clientId.toString())
            .subscribe(result => {
              this.client = {
                clientName: result.clientName,
                clientFullName: result.clientFullName,
                clientId: result.clientId,
                clientFolderURL: '',
                clientCan: result.can
              };

              this.initializeRowData();

              this.inititializeHeaderItem();
            });
        }
      );
  }

  inititializeHeaderItem() {
    const data = {
      client: this.client,
      answerSetName: this.answerSetName,
      questionnaireName: this.questionnaireName,
    };
    this.configureHeaderItems();

    this.headerItems.data = data;
    this.headerService.configureHeaderItems(this.headerItems);
  }

  updateSessionID() {
    this.sessionID = this.generateRandomSessionID();
    this.guidanceNotesService.getSession(this.sessionID).subscribe({
      next: event => this.addSessionToModel(event),
      error: err => this.addSessionToModel,
    });
  }

  addSessionToModel(event) {
    if (event) {
      this.updateSessionID();
    } else {
      let theme = this._localStorageService.getItem('theme');
      if (theme === 'Croner') {
        theme = 'croner';
      } else if (theme === 'Peninsula') {
        theme = 'peninsula';
      } else if (theme === 'BrightHR') {
        theme = 'bright';
      } else {
        theme = 'peninsula';
      }
      this.clientViewUrl = environment.videoDocBuilderGuidanceViewer + this.sessionID + '&department=' + this.user.selectedDepartment;
      this.model['sessionID'] = this.sessionID;
    }
    this.model['questionnaireId'] = this.questionnaireId;
    this.hasLoaded = true;
  }

  generateRandomSessionID() {
    return Math.floor(Math.random() * Math.floor(9999));
  }

  configureGrid() {
    this.dataGridConfig = {
      columnDefs: [
        {
          headerName: 'Document',
          field: 'title',
          minWidth: 350,
          editable: true
        },
        {
          headerName: 'Created By',
          field: 'createdBy',
          minWidth: 150
        },
        {
          headerName: 'Modified By',
          field: 'modifiedBy',
          minWidth: 150
        },
        {
          headerName: 'Version',
          field: 'version',
          minWidth: 70
        },
        {
          headerName: 'Status',
          field: 'status',
          minWidth: 70
        },
        {
          headerName: 'Created',
          field: 'createdDate',
          minWidth: 150
        },
        {
          headerName: 'Modified',
          field: 'modifiedDate',
          minWidth: 150
        },
        {
          headerName: '',
          field: 'fileUrl',
          cellRenderer: 'linkRenderer',
          cellRendererParams: {
            useParamsValue: true,
            linkTitle: 'View / Edit',
          },
          minWidth: 150,
          suppressSorting: true,
          suppressFilter: true
        }
      ],
      defaultColDef: {
        headerCheckboxSelection: this.isFirstColumn,
        checkboxSelection: this.isFirstColumn,
        minWidth: 100
      },
      rowSelection: 'multiple',
      frameworkComponents: {
        linkRenderer: LinkRendererComponent
      }
    };
  }

  initializeRowData() {
    this.questionnaireService.getGeneratedDocuments(this.id, this.clientId)
      .subscribe(data => {
        this.documentsRowData = data;
      });

  }

  isFirstColumn(params) {
    const displayedColumns = params.columnApi.getAllDisplayedColumns();
    const thisIsFirstColumn = displayedColumns[0] === params.column;
    return thisIsFirstColumn;
  }

  selectedRows($event) {
    this.selectedRowData = $event;
  }

  cellEditingStopped($event) {
    this.questionnaireService
      .editGeneratedDocumentName(this.clientId,$event);
  }

  submit() {
    if (!this.pristine || this.form.dirty) {
      this.save(false);
    }
  }

  private save(silent: boolean): void {
    if (this.saving) {
      this.saveQueued = true;
      this.queuedSaveSilent = this.queuedSaveSilent && silent;
      return;
    }

    const snapshot = JSON.stringify(this.model);

    this.form.markAsPristine();
    this.markAsPristine();

    if (silent && snapshot === this.lastSavedSnapshot) {
      return;
    }

    this.saving = true;

    if (silent) {
      this.setAutosaveStatus('saving');
    }

    this.questionnaireService.saveAnswerSet$(this.model, this.id, this.answerSetName, silent)
      .subscribe(
        () => {
          this.lastSavedSnapshot = snapshot;
          if (silent) {
            this.setAutosaveStatus('saved');
          }
          this.onSaveFinished();
        },
        () => {
          this.form.markAsDirty();
          this.markAsNotPristine();
          // the error interceptor shows its own notification, so just clear the autosave pop-up
          if (silent) {
            this.setAutosaveStatus(null);
          }
          this.onSaveFinished();
        }
      );
  }

  // Drives the bottom-right autosave pop-up: shown while saving, then "Saved" briefly before hiding.
  private setAutosaveStatus(status: 'saving' | 'saved' | null): void {
    clearTimeout(this.autosaveStatusTimer);
    this.autosaveStatus = status;

    if (status === 'saved') {
      this.autosaveStatusTimer = setTimeout(() => this.autosaveStatus = null, 2500);
    }
  }

  private onSaveFinished(): void {
    this.saving = false;

    if (this.saveQueued) {
      const silent = this.queuedSaveSilent;
      this.saveQueued = false;
      this.queuedSaveSilent = true;

      if (!silent || this.form.dirty || !this.pristine) {
        this.save(silent);
      }
    }
  }

  showSector() {
    return environment.showSectors;
  }

  openAddQuestionDialog() {
    this.addQuestionDialogRef = this.dialog.open(AnswerSetAddQuestionDialogComponent, {
      width: '1090px',
      panelClass: 'db-dialog',
      data: {
        tabs: this.tabs,
        answersetId: this.id
      }
    });

    this.addQuestionDialogRef.afterClosed().pipe(
    ).subscribe(result => {
      if (result) {
        this.questionnaireService.getAnswerSetData(this.id).subscribe((questionnaireResult => {
          if (questionnaireResult) {
            this.model = {};
            this.tabs = [];
            this.model = questionnaireResult[0].body;
            this.tabs = questionnaireResult[1].body;
            this.syncFormsWithTabs();
          }
        }));
      }
    });
  }

  openGeneratePDFDialog() {
    this.generatePDFDialogRef = this.dialog.open(GeneratePDFDialogComponent, {
      width: '1090px',
      panelClass: 'db-dialog',
      data: {
        client: this.client,
        answerSetId: this.id,
      }
    });
  }

  openGenerateDocumentsDialog() {
    this.generateDocumentsDialogRef = this.dialog.open(GenerateDocumentsDialogComponent, {
      width: '1090px',
      panelClass: 'db-dialog',
      data: {
        client: this.client,
        questionnaireId: this.questionnaireId,
        answerSetId: this.id,
      }
    });
  }

  deleteSelectedDocuments() {
    this.confirmDialogService
      .confirm('Confirm', 'Are you sure you want to delete the selected document(s)?', 'Delete')
      .subscribe(
        result => {
          if (result) {
            let hasPublishedDocs = false;

            const idsToDelete = this.selectedRowData
              .filter(row => row.status === 'Draft')
              .map(row => row.id.toString());

            hasPublishedDocs = this.selectedRowData.some(row => row.status === 'Published');

            if (hasPublishedDocs == true){
              this.notificationService.notification(
                new Notification(
                  'warning', 'An Error Occured', 'DocBuilder cannot delete published documents. Please go to document source to delete'
                )
              );
            }

            if (idsToDelete.length > 0) {
              this.questionnaireService.DeleteGeneratedDocuments(idsToDelete, this.clientId)
              .subscribe(data => {
                if (data && data === 'true') {
                  this.initializeRowData();
                  this.selectedRowData = [];
                } else {
                  this.notificationService.notification(
                    new Notification(
                      'danger', 'An Error Occured', 'Please select a document and try again, if the problem persists contact Service Desk.'
                    )
                  );
                }
              });
            }
          }
        }
      );
  }

  publishDocuments() {
    const idsToPublish = this.selectedRowData.map(row => row.id.toString());

    this.questionnaireService.PublishGeneratedDocuments(idsToPublish, this.clientId)
      .subscribe(data => {
        if (data && data === 'true') {
          this.initializeRowData();
          this.notificationService.notification(
            new Notification(
              'success', 'Document Published', 'The document has been successfully published.'
            )
          );
        } else if (data && data === 'NeedApproval') {
          this.notificationService.notification(
            new Notification(
              'warning', 'Publishing Requires Approval:', 'An email has been sent to your Approving Manager.'
            )
          );
        } else {
          this.notificationService.notification(
            new Notification(
              'danger', 'An Error Occured', 'Please select a document and try again, if the problem persists contact Service Desk.'
            )
          );
        }
      });
  }

  ngOnDestroy() {
    clearTimeout(this.autosaveStatusTimer);
    this.subscriptionManager.unsubscribe();

    this.onDestroy$.next();
  }

  markAsPristine(): void { this.pristine = true; };
  markAsNotPristine(): void { if (this.pristine) this.pristine = false; };

  onModelChange(): void {
    this.markAsNotPristine();
    this.modelChanged$.next();
  }

  get isAllPristine(): boolean { return this.form.pristine; }

  private syncFormsWithTabs(): void {
    while (this.form.length < this.tabs.length) {
      this.form.push(new FormGroup({}));
      this.options.push(<FormlyFormOptions>{});
    }
    while (this.form.length > this.tabs.length) {
      this.form.removeAt(this.form.length - 1);
      this.options.pop();
    }
  }
}
